import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRightIcon } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { ExamStamp } from "@/components/ledger/ExamStamp";
import { Badge } from "@/components/ledger/Badge";
import { useExam, useSubjects } from "@/lib/query-hooks";

export const Route = createFileRoute("/exam/$examSlug")({
  head: ({ params }) => ({
    meta: [
      {
        title: `${params.examSlug.toUpperCase()} — Subjects · PN-CivilServices`,
      },
      {
        name: "description",
        content: `Every subject filed under ${params.examSlug.toUpperCase()} — PYQs, NCERTs, standard books, study materials and notes.`,
      },
      {
        property: "og:title",
        content: `${params.examSlug.toUpperCase()} · PN-CivilServices`,
      },
      {
        property: "og:description",
        content: `Browse ${params.examSlug.toUpperCase()} subjects — organized like a card catalog.`,
      },
    ],
  }),
  component: ExamPage,
});

function ExamPage() {
  const { examSlug } = Route.useParams();
  const { data: exam } = useExam(examSlug);
  const { data: subjects, isLoading } = useSubjects(examSlug);

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Ledger", to: "/" },
            { label: examSlug.toUpperCase() },
          ]}
        />

        <header className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-5">
            <ExamStamp code={exam?.shortCode ?? examSlug.toUpperCase()} size="lg" />
            <div>
              <div className="label-card">Exam drawer</div>
              <h1 className="mt-1 font-display text-4xl font-semibold">
                {exam?.name ?? examSlug.toUpperCase()}
              </h1>
              {exam?.description ? (
                <p className="mt-2 max-w-xl text-ink-soft">
                  {exam.description}
                </p>
              ) : null}
            </div>
          </div>
        </header>

        <div className="mt-12 mb-3 flex items-center justify-between">
          <div className="label-card">Subjects</div>
          <div className="text-xs text-ink-muted">
            {subjects?.length ?? 0} filed
          </div>
        </div>

        <ul className="card-ledger divide-y divide-hairline overflow-hidden">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="h-16 animate-pulse bg-surface-alt/40" />
            ))
          ) : subjects?.length ? (
            subjects.map((s) => (
              <li key={s.id}>
                <Link
                  to="/exam/$examSlug/subject/$subjectSlug"
                  params={{ examSlug, subjectSlug: s.slug }}
                  className="group flex items-center gap-4 px-5 py-4 hover:bg-surface-alt/50 transition"
                >
                  <div className="label-card w-8">
                    {String((s.order ?? 0) + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold">
                      {s.name}
                    </div>
                    {s.description ? (
                      <div className="text-sm text-ink-soft line-clamp-1">
                        {s.description}
                      </div>
                    ) : null}
                  </div>
                  <div className="hidden md:flex items-center gap-2">
                    {s.moduleCount ? (
                      <Badge variant="outline">{s.moduleCount} modules</Badge>
                    ) : null}
                    {s.fileCount ? (
                      <Badge variant="default">{s.fileCount} files</Badge>
                    ) : null}
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-ink-muted group-hover:text-paprika group-hover:translate-x-0.5 transition" />
                </Link>
              </li>
            ))
          ) : (
            <li className="p-10 text-center text-ink-soft">
              No subjects filed yet.
            </li>
          )}
        </ul>
      </div>
    </AppShell>
  );
}
